
import LockRepoDialog from '@/components/dialogs/backups/LockRepoDialog.vue';
import {
  BackupRepoDto,
  BackupRepoRepositoryInterface,
  LockType,
  RepoLocksRepositoryInterface,
  RepoLocksDto,
} from '@/services/Backup/interfaces';
import { AxiosError } from 'axios';
import { Component, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import { BackupRepoId } from '@/services/Backup/interfaces/index';
import BackupRepoDetailsDialog from '@/components/dialogs/backups/BackupRepoDetailsDialog.vue';
import DeleteBackupRepoDialog from '@/components/dialogs/backups/DeleteBackupRepoDialog.vue';
import PruneBackupRepoDialog from '@/components/dialogs/backups/PruneBackupRepoDialog.vue';

@Component({
  mixins: [Themeable],
  components: {
    DeleteBackupRepoDialog,
    PruneBackupRepoDialog,
    BackupRepoDetailsDialog,
  },
  inject: ['backupRepoRepository', 'repoLocksRepository'],
})
export default class AllBackupRepos extends Vue {
  public loading: boolean = false;
  public currentPage: number = 1;
  public itemsPerPage: number = 10;
  public totalItems: number = 0;

  public backupRepoRepository!: BackupRepoRepositoryInterface;
  public repoLocksRepository!: RepoLocksRepositoryInterface;
  public backupRepos: BackupRepoDto[] = [];

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Repository ID', value: 'repositoryId' },
    { text: 'Repository Path', value: 'repositoryPath' },
    { text: 'Default For Origin Server', value: 'targetOriginServerId' },
    { text: 'Backup Server', value: 'backupServerId' },
    { text: 'Actions', value: 'actions' },
  ];

  public deleteDialog: string = '';
  public deleteDialogData: BackupRepoDto | null = null;

  public viewDialog: string = '';
  public viewDialogData: BackupRepoDto | null = null;

  public selectedRepoLock: RepoLocksDto | null = null;

  public lockDialogVisible: boolean = false;

  public async created(): Promise<void> {
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    await this.search();
  }

  public async search(): Promise<void> {
    this.loading = true;
    try {
      await this.getBackupRepoRepositories();
    } catch (err) {
      await this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  public async getBackupRepoRepositories(): Promise<void> {
    const result = await this.backupRepoRepository.getAllBackupRepos({
      page: this.currentPage,
      pageSize: this.itemsPerPage,
    });
    this.totalItems = result?.total ?? 0;
    this.backupRepos = result?.data ?? [];
  }

  public async changePage(): Promise<void> {
    await this.search();
  }

  public async showDeleteDialogFor(id: BackupRepoId): Promise<void> {
    try {
      const backupRepo = await this.backupRepoRepository.getBackupRepo(id);
      if (!backupRepo) {
        return;
      }
      this.deleteDialog = 'delete-backup-repo-dialog';
      this.deleteDialogData = backupRepo;
    } catch (err) {
      this.onError(err);
    }
  }

  public async showViewDialogFor(id: BackupRepoId): Promise<void> {
    try {
      const backupRepo = this.$store.getters.isAdmin
          ? await this.backupRepoRepository.getBackupRepoSecrets(id)
          : await this.backupRepoRepository.getBackupRepo(id);

      if (!backupRepo) {
        return;
      }

      this.viewDialog = 'backup-repo-details-dialog';
      this.viewDialogData = backupRepo;
    } catch (err) {
      this.onError(err);
    }
  }

  public async createBackup(backupRepo?: BackupRepoDto): Promise<void> {
    this.$router.push({
      name: 'backup_create',
      query: { backupRepoId: backupRepo?.id.toString() },
    });
  }

  public async createBackupRepository(): Promise<void> {
    this.$router.push({
      name: 'backup_repository_create',
    });
  }

  public async pruneRepository(backupRepo?: BackupRepoDto): Promise<void> {
    this.$router.push({
      name: 'prune_create',
      query: { backupRepoId: backupRepo?.id.toString() },
    });
  }

  public async forgetRepository(backupRepo?: BackupRepoDto): Promise<void> {
    this.$router.push({
      name: 'forget_create',
      query: { backupRepoId: backupRepo?.id.toString() },
    });
  }

  public async viewLockRepos(backupRepo?: BackupRepoDto): Promise<void> {
    this.$router.push({
      name: 'view_locks',
      query: { backupRepoId: backupRepo?.id.toString() },
    });
  }

  public async deleteBackupRepo(backupRepo: BackupRepoDto): Promise<void> {
    try {
      await this.backupRepoRepository.deleteBackupRepo(backupRepo.id);
      this.$successDialog.show('Backup repo deleted successfully');
      await this.search();
    } catch (err) {
      this.onError(err);
    }
  }

  private async onError(err: unknown): Promise<void> {
    console.log(err);
    const asAxiosError = err as AxiosError;
    if (asAxiosError.isAxiosError) {
      this.$errorDialog.show(asAxiosError.response?.data?.message ?? 'An axios error occurred');
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    } else {
      this.$errorDialog.show('An unknown error occurred');
    }
  }
}
